import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import {
    IdNameValueObject,
    VehicleSpecCategory,
    VehicleSpecification,
    SearchCriteria,
    Vehicle,
    VehicleHistory,
} from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

const vehicleAPIExternalEndpoints = {
    getMakes: 'https://vpic.nhtsa.dot.gov/api/vehicles/getallmakes?format=json',
    getModelsByMakeAndYear: 'https://vpic.nhtsa.dot.gov/api/vehicles/getmodelsformakeyear/make/',
};

@Injectable({
    providedIn: 'root',
})
export class VehicleApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    save(tenantId: string, vehicle: Vehicle): Observable<Vehicle> {
        if (!vehicle.id || vehicle.id === null) return this.http.post<Vehicle>(this.serviceUtils.getBaseUrl('vehicle', tenantId), vehicle);

        return this.http.put<Vehicle>(this.serviceUtils.getBaseUrl('vehicle', tenantId), vehicle);
    }

    delete(tenantId: string, id: string): Observable<object> {
        return this.http.delete(this.serviceUtils.getBaseUrl('vehicle', tenantId) + `/${id}`);
    }

    get(tenantId: string, id: string | null, state?: string): Observable<Vehicle> {
        if (!id || id === null)
            return of(<Vehicle>{
                id: id,
                tenantId: tenantId,
                state: state,
                year: new Date().getFullYear(),
            });

        return this.http.get<Vehicle>(this.serviceUtils.getBaseUrl('vehicle', tenantId) + `/${id}`);
    }

    /**
     * The specification categories the provider holds, for a browse-then-drill-down view. Empty when
     * the vehicle has none.
     */
    getSpecificationCategories(tenantId: string, id: string): Observable<VehicleSpecCategory[]> {
        return this.http.get<VehicleSpecCategory[]>(
            this.serviceUtils.getBaseUrl('vehicle', tenantId) + `/${id}/specification/categories`,
            { headers: this.httpHeadersWithNoLoading },
        );
    }

    /**
     * Specifications for one category, or every category when categoryId is omitted. A transport
     * failure is left to error so the caller can tell "none published" from "could not check" and
     * offer a retry; it must not be reported as the former.
     */
    getSpecifications(tenantId: string, id: string, categoryId?: number): Observable<VehicleSpecification[]> {
        const path = categoryId === undefined ? `/${id}/specification` : `/${id}/specification/${categoryId}`;

        return this.http.get<VehicleSpecification[]>(this.serviceUtils.getBaseUrl('vehicle', tenantId) + path, {
            headers: this.httpHeadersWithNoLoading,
        });
    }

    getByCustomerId(tenantId: string, customerId: string | null, showLoading = true): Observable<Vehicle[]> {
        if (customerId === null) return of([]);

        return this.http.get<Vehicle[]>(this.serviceUtils.getBaseUrl('vehicle', tenantId) + `/customer/${customerId}`, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    //TODO - Implement this method to fetch all makes from the Raptor API
    getAllMakes(): Observable<IdNameValueObject[]> {
        return this.http
            .get<{ Results: { Make_ID: string; Make_Name: string }[] }>(vehicleAPIExternalEndpoints.getMakes)
            .pipe(map((response) => response.Results.map((n) => <IdNameValueObject>{ id: n.Make_ID, name: n.Make_Name })));
    }

    //TODO - Implement this method to fetch all makes from the Raptor API
    getModelsByMakeAndYear(make: string, year: number): Observable<IdNameValueObject[]> {
        return this.http
            .get<{
                Results: { Model_ID: string; Model_Name: string }[];
            }>(`${vehicleAPIExternalEndpoints.getModelsByMakeAndYear}${make.toLowerCase()}/modelyear/${year}?format=json`)
            .pipe(map((response) => response.Results.map((n) => <IdNameValueObject>{ id: n.Model_ID, name: n.Model_Name })));
    }

    getInformationHistory(
        tenantId: string,
        id: string,
        customerId: string | null = null,
        showLoading = true,
    ): Observable<{ vin: string; records: VehicleHistory[] }> {
        return this.http.get<{ vin: string; records: VehicleHistory[] }>(
            this.serviceUtils.getBaseUrl('vehicle', tenantId) +
                `/information-history/${customerId ? `vehicle/${id}/customer/${customerId}/` : `${id}`}`,
            {
                headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
            },
        );
    }

    getNationalInformationHistory(tenantId: string, vin: string, showLoading = true): Observable<{ vin: string; records: VehicleHistory[] }> {
        return this.http.get<{ vin: string; records: VehicleHistory[] }>(
            this.serviceUtils.getBaseUrl('vehicle', tenantId) + `/national-information-history-vin/${vin}`,
            { headers: showLoading ? undefined : this.httpHeadersWithNoLoading },
        );
    }

    getNationalInformationByImage(tenantId: string, state: string, image: Blob): Observable<Vehicle[]> {
        const form = new FormData();
        form.append('state', state);
        form.append('file', image, 'vehicle-image');

        return this.http.post<Vehicle[]>(this.serviceUtils.getBaseUrl('vehicle', tenantId, undefined, 'v2') + '/national-information', form);
    }

    getNationalInformationByLicensePlate(tenantId: string, state: string, licensePlate: string): Observable<Vehicle> {
        return this.http.get<Vehicle>(
            this.serviceUtils.getBaseUrl('vehicle', tenantId) +
                `/national-information/state/${state}/license-plate/${encodeURIComponent(licensePlate)}`,
        );
    }

    getNationalInformationByVin(tenantId: string, vin: string): Observable<Vehicle> {
        return this.http.get<Vehicle>(this.serviceUtils.getBaseUrl('vehicle', tenantId) + '/national-information/vin/' + encodeURIComponent(vin));
    }

    getDeclinedServicesHistory(
        tenantId: string,
        id: string,
        customerId: string | null = null,
        showLoading = true,
    ): Observable<{ vin: string; records: VehicleHistory[] }> {
        return this.http.get<{ vin: string; records: VehicleHistory[] }>(
            this.serviceUtils.getBaseUrl('vehicle', tenantId) +
                `/declined-services-history/${customerId ? `vehicle/${id}/customer/${customerId}` : `${id}`}`,
            {
                headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
            },
        );
    }

    search(tenantId: string, criteria: SearchCriteria): Observable<Vehicle[]> {
        const url = this.serviceUtils.getBaseUrl('vehicle', tenantId) + '/search';
        return this.http.post<Vehicle[]>(url, criteria);
    }

    searchByImage(tenantId: string, image: Blob): Observable<Vehicle[]> {
        const form = new FormData();
        form.append('file', image, 'vehicle-image');

        return this.http.post<Vehicle[]>(this.serviceUtils.getBaseUrl('vehicle', tenantId, undefined, 'v2') + '/search/image', form);
    }

    searchByKeyword(tenantId: string, keyword: string, pageNumber: number, pageSize: number): Observable<Vehicle[]> {
        const body = { pageSize, pageNumber, items: [], orderBy: 'customers.name' };

        let url = this.serviceUtils.getBaseUrl('vehicle', tenantId) + '/search/keyword';
        if (keyword && keyword.length > 0) url += `/${encodeURIComponent(keyword)}`;
        return this.http.post<Vehicle[]>(url, body);
    }
}
